// Application Service - Saves form submissions directly to Firestore for Admin panel
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Convert a File to base64 data URI for storage in Firestore (no Storage needed)
 */
const fileToBase64 = (file) => {
  return new Promise((resolve) => {
    if (!file || !(file instanceof File)) { resolve(null); return; }
    // Skip files larger than 800KB to stay within Firestore's 1MB doc limit
    if (file.size > 800 * 1024) { 
      console.warn('File too large for Firestore storage:', file.name, (file.size / 1024).toFixed(0) + 'KB');
      resolve(null); 
      return; 
    }
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, size: file.size, data: reader.result });
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
};

/**
 * Recursively clean an object for Firestore storage
 * - Converts undefined to null
 * - Strips File, Blob, Function, and other non-serializable types
 * - Converts nested arrays to JSON strings (Firestore doesn't support arrays in arrays)
 */
const sanitizeForFirestore = (obj, depth = 0) => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj === 'function') return null;
  if (typeof obj === 'symbol') return null;
  if (typeof obj !== 'object') return obj;
  if (typeof File !== 'undefined' && obj instanceof File) return obj.name || null;
  if (typeof Blob !== 'undefined' && obj instanceof Blob) return null;
  if (obj instanceof Date) return obj.toISOString();
  if (obj instanceof RegExp) return obj.toString();
  
  if (Array.isArray(obj)) {
    // Check if any element is an array (nested array — not allowed in Firestore)
    const hasNestedArray = obj.some(item => Array.isArray(item));
    if (hasNestedArray) {
      // Convert the whole array to a JSON string
      return JSON.stringify(obj);
    }
    return obj.map(item => sanitizeForFirestore(item, depth + 1));
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      cleaned[key] = null;
    } else if (typeof value === 'function' || typeof value === 'symbol') {
      // skip
    } else if (typeof File !== 'undefined' && value instanceof File) {
      cleaned[key] = value.name || null;
    } else if (typeof Blob !== 'undefined' && value instanceof Blob) {
      // skip
    } else if (typeof value === 'object' && value !== null) {
      cleaned[key] = sanitizeForFirestore(value, depth + 1);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

/**
 * Submit Field Worker Application — saves directly to Firestore
 */
export const submitFieldWorkerApplication = async (formData) => {
  const applicationId = 'FW-' + Date.now().toString(36).toUpperCase();

  try {
    const cleanData = sanitizeForFirestore(formData);
    console.log('Saving field worker data, size:', JSON.stringify(cleanData).length, 'bytes');
    await addDoc(collection(db, 'applications'), {
      applicationId,
      applicationType: 'field-worker',
      status: 'new',
      formData: cleanData,
      submittedAt: serverTimestamp(),
      submittedAtLocal: new Date().toLocaleString('en-PH', { timeZone: 'Asia/Manila' }),
    });
    console.log('Field Worker application saved:', applicationId);
    return { success: true, applicationId };
  } catch (error) {
    console.error('Firestore error:', error.code, error.message);
    throw new Error(`Submission failed: ${error.message}`);
  }
};

/**
 * Submit Office Staff Application — saves directly to Firestore
 */
export const submitOfficeStaffApplication = async (formData) => {
  const applicationId = 'OS-' + Date.now().toString(36).toUpperCase();

  try {
    // Convert files to base64 for Firestore storage (no Firebase Storage needed)
    const [resumeBase64, excelBase64] = await Promise.all([
      fileToBase64(formData.resumeFile),
      fileToBase64(formData.excelFile),
    ]);

    const cleanData = sanitizeForFirestore(formData);
    // Attach base64 file data
    if (resumeBase64) cleanData.resumeFileData = resumeBase64;
    if (excelBase64) cleanData.excelFileData = excelBase64;
    
    console.log('Saving office staff data, size:', JSON.stringify(cleanData).length, 'bytes');
    await addDoc(collection(db, 'applications'), {
      applicationId,
      applicationType: 'office-staff',
      status: 'new',
      formData: cleanData,
      submittedAt: serverTimestamp(),
      submittedAtLocal: new Date().toLocaleString('en-PH', { timeZone: 'Asia/Manila' }),
    });
    console.log('Office Staff application saved:', applicationId);
    return { success: true, applicationId };
  } catch (error) {
    console.error('Firestore error:', error.code, error.message);
    throw new Error(`Submission failed: ${error.message}`);
  }
};

/**
 * Save quote request directly to Firestore
 */
export const saveQuoteToFirestore = async (quoteData) => {
  const quoteId = 'QR-' + Date.now().toString().slice(-8);

  try {
    const cleanData = sanitizeForFirestore(quoteData);
    const docRef = await addDoc(collection(db, 'quoteRequests'), {
      quoteId,
      status: 'new',
      ...cleanData,
      submittedAt: serverTimestamp(),
      submittedAtLocal: new Date().toLocaleString('en-PH', { timeZone: 'Asia/Manila' }),
    });
    console.log('Quote saved to Firestore:', docRef.id);
    return { docId: docRef.id, quoteId };
  } catch (error) {
    console.error('Error saving quote:', error);
    throw new Error('Failed to submit quote request. Please try again.');
  }
};
